import moment from 'moment';

export const isEmail = (email: string): boolean => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const isImg = (url: string): boolean => {
  return /(.*?)\.(png|jpe?g|gif|webp)/i.test(url);
};

export const isVideo = (url: string): boolean => {
  return /(.*?)\.(swf|avi|flv|mpg|rm|mov|wav|asf|3gp|mkv|rmvb|mp4)/i.test(url);
};

export const isMobile: boolean =
  /Android|webOS|iPhone|iPod|BlackBerry|iPad/i.test(navigator.userAgent);

// 密码长度在8到20个字符之间
export const isPassword = (password: string): boolean => {
  return /^.{8,20}$/.test(password);
};

export const nFormatter = (num: number, digits: number): string => {
  const lookup = [
    { value: 1, symbol: '' },
    { value: 1e3, symbol: 'k' },
    { value: 1e6, symbol: 'm' },
    { value: 1e9, symbol: 'G' },
    { value: 1e12, symbol: 'T' },
    { value: 1e15, symbol: 'P' },
    { value: 1e18, symbol: 'E' },
  ] as { value: number; symbol: string }[];
  const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/;
  // @ts-ignore
  const item = lookup?.findLast(
    (item: { value: number; symbol: string }) => num >= item.value
  );
  return item
    ? (num / item.value).toFixed(2).replace(regexp, '').concat(item.symbol)
    : '0';
};

export const formatTimeToSeconds = (time: string): number => {
  const parts = time.split(':').map(Number);
  if (parts.length === 3) {
    if (parts[2] >= 60) {
      parts[1] += Math.floor(parts[2] / 60);
      parts[2] %= 60;
    }
    if (parts[1] >= 60) {
      parts[0] += Math.floor(parts[1] / 60);
      parts[1] %= 60;
    }
  } else if (parts.length === 2) {
    if (parts[1] >= 60) {
      parts[0] += Math.floor(parts[1] / 60);
      parts[1] %= 60;
    }
    parts.unshift(0); // 添加小时部分为0
  }
  const duration = moment.duration({
    hours: parts[0] || 0,
    minutes: parts[1] || 0,
    seconds: parts[2] || 0,
  });
  return duration.asSeconds();
};

export const getDomain = (): string => {
  if (location.hostname === 'localhost') {
    return location.hostname;
  } else {
    const root = location.hostname?.split('.')[0];
    const domain = location.hostname?.replace(root, '');
    return domain;
  }
};

export const getQueryString = (name: string): string => {
  const url = window.location.search as string;

  const theRequest = {} as any;
  if (url.indexOf('?') !== -1) {
    const str = url.substr(1);
    const strs = str.split('&');

    for (let i = 0; i < strs.length; i++) {
      theRequest[strs[i].split('=')[0]] = unescape(strs[i].split('=')[1]);
    }
  }

  return theRequest?.[name];
};

export const getPathLastName = (path?: string): string => {
  const name = (path || location.pathname)
    .split('/')
    ?.slice(-1)
    ?.shift() as string;
  return name;
};

export const replaceUrlParam = (
  url: string,
  paramName: string,
  paramValue: string | number
): string => {
  const pattern = new RegExp(`(${paramName}=)([^&]*)`);

  if (url?.match(pattern)) {
    return url.replace(pattern, `${paramName}=${paramValue}`);
  }

  if (url?.indexOf('?') === -1) {
    return `${url}?${paramName}=${paramValue}`;
  }

  return `${url}&${paramName}=${paramValue}`;
};

export const objectToUrlParamStr = (obj: any): string => {
  let paramStr = '';
  for (const key in obj) {
    const value = encodeURIComponent(obj[key]);
    paramStr += `${key}=${value}&`;
  }
  paramStr = paramStr.substring(0, paramStr.length - 1);
  return paramStr;
};

export const downloadVideo = async (src: string) => {
  const res = await fetch(src);
  const blob = await res.blob();

  const name = src.split('/').pop();
  const a = document.createElement('a');
  document.body.appendChild(a);
  a.style.display = 'none';
  const url = window.URL.createObjectURL(blob);
  a.download = name || `video_${Date.now()}.mp4`;
  a.href = url;
  a.click();
};

export const getRandomStr = (length: number, str?: string) => {
  let result = '';
  const characters =
    str || 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  const charactersLength = characters.length;
  for (let i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() * charactersLength));
  }
  return result;
};

export const formatTime = (sec: number, format = 'm:ss', isDay?: boolean) => {
  const currentTime = moment.duration(sec, 'seconds');

  let timeStr = moment({
    h: currentTime.hours(),
    m: currentTime.minutes(),
    s: currentTime.seconds(),
  }).format(format) as any;

  if (isDay) {
    timeStr = timeStr.split(':') as string[];
    timeStr[0] = currentTime.days() * 24 + currentTime.hours();
    timeStr = timeStr.join(':');
  }

  return timeStr;
};

export const firstWordCapitalize = (str: string): string => {
  if (str?.length) {
    return str?.[0].toUpperCase() + str?.substring(1);
  } else {
    return str;
  }
};

export const calculateContentLen = (str: string) => {
  let englishLength = 0;
  let chineseLength = 0;
  let otherLength = 0;

  for (let i = 0; i < str.length; i++) {
    // 常见符号、大小英文
    if (str.charCodeAt(i) >= 48 && str.charCodeAt(i) <= 122) {
      englishLength++;
    } else if (str.charCodeAt(i) >= 0x4e00 && str.charCodeAt(i) <= 0x9fff) {
      chineseLength++;
    } else {
      otherLength++;
    }
  }

  return {
    englishLength,
    chineseLength,
    otherLength,
  };
};

export const getBrowserInfo = () => {
  const userAgent = window.navigator.userAgent;

  return {
    isAndroid: /android/gi.test(userAgent) /* 安卓*/,
    isIOs: /iphone|ipod|ios/gi.test(userAgent) /* IOS */,
    isWeChat: /MicroMessenger/gi.test(userAgent) /* 微信 */,
    isQQ: /mobile.*qq/gi.test(userAgent) /* QQ */,
    isUC: /ucbrowser/gi.test(userAgent) /* UC浏览器 */,
    isQuark: /Quark/gi.test(userAgent) /* 夸克浏览器 */,
    isQQBrowser: /mqqbrowser[^LightApp]/gi.test(userAgent) /* QQ浏览器 */,
    isQQBrowserLight: /MQQBrowserLightApp/gi.test(userAgent) /* qq轻浏览器*/,

    isTrident: /Trident/gi.test(userAgent) /**IE内核 */,
    isPresto: /Presto/gi.test(userAgent) /** opera内核 */,
    iswebKit: /AppleWebKit/gi.test(userAgent) /** 苹果、谷歌内核 */,
    isGecko: /Gecko.*KHTML/gi.test(userAgent) /** 火狐内核 */,
    isSafari:
      /Safari/.test(userAgent) && !/Chrome/.test(userAgent) /** Safari内核 */,

    isMobile: !!userAgent.match(/AppleWebKit.*Mobile.*/) /**是否为移动终端 */,
    isIos: !!userAgent.match(/\(i[^;]+;( U;)? CPU.+Mac OS X/) /* ios终端 */,
    isAndroidMobile:
      userAgent.indexOf('Android') > -1 || userAgent.indexOf('Adr') > -1, //android终端
    isIPhone: userAgent.indexOf('iPhone') > -1, //iPhone或者QQHD浏览器
    isIPad: userAgent.indexOf('iPad') > -1, //iPad
  };
};

export const addArrayUniqueElement = (
  arr: string[],
  str: string
): string[] | string[] => {
  if (!arr.includes(str)) {
    arr.push(str);
  }
  return arr;
};

export const getAssetsSvg = (name: string) => {
  return new URL(`../assets/svg/${name}`, import.meta.url).href;
};

export const removeURLParameter = (parameter: string) => {
  // 获取当前的 URL
  const url = new URL(window.location.href);
  // 从 URL 中移除指定的参数
  url.searchParams.delete(parameter);
  // 使用 history.replaceState 更新 URL，但不触发页面刷新
  window.history.replaceState({}, document.title, url.toString());
};

export function updateUrlParameter(
  params = {} as { [key: string]: any },
  replace = false
) {
  // 获取当前 URL 的查询参数
  const urlParams = new URLSearchParams(window.location.search);

  // 遍历传入的参数对象，将其添加或更新到 URL 中
  Object.keys(params).forEach((key: string) => {
    urlParams.set(key, params[key]);
  });

  // 生成新的 URL
  const newUrl = `${window.location.pathname}?${urlParams.toString()}`;

  // 使用 pushState 或 replaceState 更新 URL
  if (replace) {
    window.history.replaceState({ path: newUrl }, '', newUrl);
  } else {
    window.history.pushState({ path: newUrl }, '', newUrl);
  }
}

export const getHomeUrl = () => {
  let url = '/avatar';

  if (location.host === 'www.visionstory.ai') {
    const locale = localStorage.getItem('locale');
    const code = localStorage.getItem('locale')?.split('-')?.[0];

    if (locale?.includes('zh')) {
      url = `https://www.visionstory.ai/${locale}/home`;
    } else if (code) {
      url = `https://www.visionstory.ai/${code}/home`;
    } else {
      url = 'https://www.visionstory.ai/home';
    }
  }

  return url;
};
